import { ApiClient } from '../apiClient';

export interface LinkWithStats {
  id: string;
  name: string;
  lastAccessed: string;
  createdAt: string;
  viewCount: number;
}

export interface DashboardMetrics {
  totalViews: number;
  activeLinks: number;
  totalBandwidth: number;
}

export interface ViewsAnalytics {
  hourlyViews: Record<string, number>;
  deviceStats: Record<string, number>;
  geoStats: Record<string, number>;
}

export interface RecentLinksResponse {
  recentLinks: LinkWithStats[];
  popularLinks: LinkWithStats[];
}

export const AnalyticsService = {
  getMetrics: async (): Promise<DashboardMetrics> => {
    try {
      const response = await ApiClient.get<DashboardMetrics>('/analytics/metrics');
      if (!response.data) {
        throw new Error('Failed to fetch dashboard metrics');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw error;
    }
  },

  getRecentLinks: async (): Promise<RecentLinksResponse> => {
    try {
      const response = await ApiClient.get<RecentLinksResponse>('/recentlinks');
      if (!response.data) {
        throw new Error('Failed to fetch recent links');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching recent links:', error);
      throw error;
    }
  },

  getViewsAnalytics: async (): Promise<ViewsAnalytics> => {
    try {
      const response = await ApiClient.get<ViewsAnalytics>('/analytics/views');
      if (!response.data) {
        throw new Error('Failed to fetch views analytics');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching views analytics:', error);
      throw error;
    }
  },

  getLinkAnalytics: async (
    id: string,
    startDate: Date,
    endDate: Date
  ): Promise<{
    dailyViews: Record<string, number>;
    deviceStats: Record<string, number>;
    geoStats: Record<string, number>;
  }> => {
    try {
      const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });

      const response = await ApiClient.get<{
        dailyViews: Record<string, number>;
        deviceStats: Record<string, number>;
        geoStats: Record<string, number>;
      }>(`/links/${id}/analytics?${params.toString()}`);

      if (!response.data) {
        throw new Error(`Failed to fetch analytics for link with ID: ${id}`);
      }

      return response.data;
    } catch (error) {
      console.error(`Error fetching analytics for link with ID ${id}:`, error);
      throw error;
    }
  }
}; 